import { useState } from "react";
import {
  portfolio as api,
  type Account,
  type AccountType,
  type Investor,
} from "../../api";
import { Link } from "../../router";
import { formatCurrency } from "./format";

// The manual account-opening flow: wrapper → suitability → KYC → funding →
// review. Mirrors a Nordic onboarding (minus BankID — the advisor attests
// identity in the KYC checklist).

const CURRENCIES = ["NOK", "SEK", "DKK", "EUR", "USD", "GBP"];

const TYPE_META: Record<AccountType, { label: string; hint: string }> = {
  ask: {
    label: "ASK",
    hint: "Aksjesparekonto — NOK only; EEA instruments, funds ≥80% equity",
  },
  ips: {
    label: "IPS",
    hint: "Individuell pensjonssparing — locked until retirement",
  },
  ordinary: {
    label: "Ordinary",
    hint: "Standard investment account, no wrapper restrictions",
  },
};

interface Question {
  text: string;
  options: { label: string; score: number }[];
}

const QUESTIONS: Question[] = [
  {
    text: "How long until the investor expects to need this money?",
    options: [
      { label: "Under 1 year", score: 1 },
      { label: "1–3 years", score: 2 },
      { label: "3–7 years", score: 3 },
      { label: "7–15 years", score: 4 },
      { label: "Over 15 years", score: 5 },
    ],
  },
  {
    text: "How much investment experience does the investor have?",
    options: [
      { label: "None", score: 1 },
      { label: "Bank savings and funds", score: 2 },
      { label: "Funds and listed shares", score: 3 },
      { label: "Broad, incl. ETFs and foreign markets", score: 4 },
      { label: "Extensive, incl. derivatives", score: 5 },
    ],
  },
  {
    text: "The portfolio falls 20% in a quarter. What does the investor do?",
    options: [
      { label: "Sell everything", score: 1 },
      { label: "Sell part of the holdings", score: 2 },
      { label: "Hold and wait it out", score: 3 },
      { label: "Buy more at the lower price", score: 5 },
    ],
  },
  {
    text: "How large a share of the investor's net worth will this be?",
    options: [
      { label: "More than 75%", score: 1 },
      { label: "50–75%", score: 2 },
      { label: "25–50%", score: 3 },
      { label: "10–25%", score: 4 },
      { label: "Under 10%", score: 5 },
    ],
  },
  {
    text: "What is the primary objective?",
    options: [
      { label: "Preserve capital", score: 1 },
      { label: "Modest, stable growth", score: 2 },
      { label: "Steady long-term growth", score: 3 },
      { label: "Strong growth, tolerating swings", score: 4 },
      { label: "Maximum growth", score: 5 },
    ],
  },
];

export const RISK_LABELS: Record<number, string> = {
  1: "Very cautious",
  2: "Cautious",
  3: "Balanced",
  4: "Growth",
  5: "Aggressive",
};

const STEPS = ["Account", "Suitability", "KYC", "Funding", "Review"];

export function OpenAccountWizard({
  investor,
  onClose,
  onDone,
}: {
  investor: Investor;
  onClose: () => void;
  onDone: (account: Account) => void;
}) {
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Step 1 — account
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("NOK");
  const [type, setType] = useState<AccountType>("ask");

  // Step 2 — suitability
  const [answers, setAnswers] = useState<(number | null)[]>(
    QUESTIONS.map(() => null),
  );

  // Step 3 — KYC
  const [idVerified, setIdVerified] = useState(false);
  const [pepChecked, setPepChecked] = useState(false);
  const [sourceOfFunds, setSourceOfFunds] = useState(false);

  // Step 4 — funding
  const [deposit, setDeposit] = useState("");
  const [mandateAccount, setMandateAccount] = useState("");
  const [mandateLimit, setMandateLimit] = useState("");

  const profile = computeProfile(answers);
  const kycComplete = idVerified && pepChecked && sourceOfFunds;

  function next() {
    setError(null);
    if (step === 0) {
      if (!name.trim()) return setError("Account name is required.");
      if (type === "ask" && currency !== "NOK") {
        return setError("An aksjesparekonto must be a NOK account.");
      }
    }
    if (step === 1 && profile === null) {
      return setError("Answer all questions to assess suitability.");
    }
    if (step === 3) {
      if (deposit && (!Number.isFinite(Number(deposit)) || Number(deposit) < 0)) {
        return setError("Deposit must be a positive number.");
      }
      if (mandateAccount.trim() && (!mandateLimit || Number(mandateLimit) <= 0)) {
        return setError("A funding mandate needs a positive per-pull limit.");
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.openAccount(investor.id, {
        name: name.trim(),
        currency,
        type,
        risk_profile: profile!,
        kyc: {
          id_verified: idVerified,
          pep_checked: pepChecked,
          source_of_funds: sourceOfFunds,
        },
        initial_deposit: deposit ? Number(deposit) : null,
        mandate: mandateAccount.trim()
          ? { bank_account: mandateAccount.trim(), limit: Number(mandateLimit) }
          : null,
      });
      onDone(result.account);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>Open account</h2>
        <nav className="wizard-steps">
          {STEPS.map((s, i) => (
            <span
              key={s}
              className={
                i === step
                  ? "wizard-step active"
                  : i < step
                    ? "wizard-step done"
                    : "wizard-step"
              }
            >
              {s}
            </span>
          ))}
        </nav>

        {step === 0 && (
          <div className="card-form">
            <label className="field">
              <span>Account type</span>
              <div className="seg">
                {(Object.keys(TYPE_META) as AccountType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={type === t ? "seg-option active" : "seg-option"}
                    onClick={() => {
                      setType(t);
                      if (t === "ask") setCurrency("NOK");
                    }}
                  >
                    {TYPE_META[t].label}
                  </button>
                ))}
              </div>
              <span className="field-hint">{TYPE_META[type].hint}</span>
            </label>
            <div className="field-row">
              <label className="field field-grow">
                <span>Account name *</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Aksjesparekonto, Pension"
                  autoFocus
                />
              </label>
              <label className="field field-zip">
                <span>Currency</span>
                <select
                  value={currency}
                  disabled={type === "ask"}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="card-form">
            {investor.risk_profile != null && (
              <p className="muted">
                Current profile: {investor.risk_profile} —{" "}
                {RISK_LABELS[investor.risk_profile]}. Answers below replace it.
              </p>
            )}
            {QUESTIONS.map((q, qi) => (
              <fieldset key={qi} className="field quiz-question">
                <span>{q.text}</span>
                <div className="option-list">
                  {q.options.map((o, oi) => (
                    <label
                      key={oi}
                      className={
                        answers[qi] === oi ? "option-row selected" : "option-row"
                      }
                    >
                      <input
                        type="radio"
                        name={`q${qi}`}
                        checked={answers[qi] === oi}
                        onChange={() =>
                          setAnswers((a) => a.map((v, i) => (i === qi ? oi : v)))
                        }
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
            {profile !== null && (
              <div className="quiz-result">
                Assessed risk profile: <strong>{profile} — {RISK_LABELS[profile]}</strong>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="card-form">
            <p className="muted">
              Customer due diligence under hvitvaskingsloven. All items must be
              confirmed before the investor can trade; otherwise the account
              opens with KYC pending.
            </p>
            <div className="option-list">
              <label className={idVerified ? "option-row selected" : "option-row"}>
                <input
                  type="checkbox"
                  checked={idVerified}
                  onChange={(e) => setIdVerified(e.target.checked)}
                />
                Identity verified (passport / BankID)
              </label>
              <label className={pepChecked ? "option-row selected" : "option-row"}>
                <input
                  type="checkbox"
                  checked={pepChecked}
                  onChange={(e) => setPepChecked(e.target.checked)}
                />
                PEP & sanctions screening performed
              </label>
              <label
                className={sourceOfFunds ? "option-row selected" : "option-row"}
              >
                <input
                  type="checkbox"
                  checked={sourceOfFunds}
                  onChange={(e) => setSourceOfFunds(e.target.checked)}
                />
                Source of funds documented
              </label>
            </div>
            <div className={kycComplete ? "quiz-result" : "quiz-result pending"}>
              KYC outcome:{" "}
              <strong>{kycComplete ? "Approved" : "Pending — trading blocked"}</strong>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="card-form">
            <label className="field">
              <span>Initial deposit ({currency})</span>
              <input
                type="number"
                min="0"
                step="any"
                value={deposit}
                onChange={(e) => setDeposit(e.target.value)}
                placeholder="0"
              />
            </label>
            <p className="muted">
              Optionally link a funding mandate (engangsfullmakt / AvtaleGiro) so
              buy orders can pull money from the investor's bank account instead
              of requiring deposits first.
            </p>
            <div className="field-row">
              <label className="field field-grow">
                <span>Bank account</span>
                <input
                  type="text"
                  value={mandateAccount}
                  onChange={(e) => setMandateAccount(e.target.value)}
                  placeholder="1503.42.18871"
                  maxLength={50}
                />
              </label>
              <label className="field field-grow">
                <span>Per-pull limit ({currency})</span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={mandateLimit}
                  onChange={(e) => setMandateLimit(e.target.value)}
                />
              </label>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="card-form">
            <dl className="info-grid">
              <Row label="Investor" value={investor.name} />
              <Row label="Account" value={`${name} (${currency})`} />
              <Row label="Type" value={TYPE_META[type].hint.split(" — ")[0]!} />
              <Row
                label="Risk profile"
                value={profile != null ? `${profile} — ${RISK_LABELS[profile]}` : "—"}
              />
              <Row
                label="KYC"
                value={kycComplete ? "Approved" : "Pending — trading blocked"}
              />
              <Row
                label="Initial deposit"
                value={
                  deposit ? formatCurrency(Number(deposit), currency) : "None"
                }
              />
              <Row
                label="Funding mandate"
                value={
                  mandateAccount.trim()
                    ? `${mandateAccount} · up to ${formatCurrency(Number(mandateLimit), currency)} per pull`
                    : "None"
                }
              />
            </dl>
            <p className="muted">
              Opening the account files a signed agreement under{" "}
              <Link to={`/app/crm/${investor.id}/documents`} className="link-action">
                {investor.name}'s documents
              </Link>
              .
            </p>
          </div>
        )}

        {error && <div className="banner-error">{error}</div>}

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          {step > 0 && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setStep((s) => s - 1)}
              disabled={busy}
            >
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" className="btn btn-primary" onClick={next}>
              Continue
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={submit}
              disabled={busy}
            >
              {busy ? "Opening…" : "Open account"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="info-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function computeProfile(answers: (number | null)[]): number | null {
  if (answers.some((a) => a === null)) return null;
  const scores = answers.map((a, i) => QUESTIONS[i]!.options[a!]!.score);
  const avg = scores.reduce((s, x) => s + x, 0) / scores.length;
  return Math.min(5, Math.max(1, Math.round(avg)));
}
